import { motion } from 'framer-motion';
import { useInView } from 'framer-motion';
import { useRef } from 'react';

const AboutSectionV2 = () => {
  const ref = useRef(null);
  const isInView = useInView(ref, { once: true, margin: '-100px' });

  return (
    <section
      id="about"
      ref={ref}
      data-testid="about-section-v2"
      className="py-24 md:py-32 bg-white relative overflow-hidden"
    >
      {/* Decorative elements */}
      <div className="absolute top-0 right-0 w-96 h-96 bg-gradient-to-bl from-[#5B8FB9]/10 to-transparent rounded-full -translate-y-1/2 translate-x-1/2" />
      <div className="absolute bottom-0 left-0 w-64 h-64 bg-gradient-to-tr from-[#D4A5B5]/10 to-transparent rounded-full translate-y-1/2 -translate-x-1/2" />
      <div className="absolute top-1/2 left-1/2 w-48 h-48 bg-[#7CB98F]/5 rounded-full -translate-x-1/2 -translate-y-1/2" />

      <div className="max-w-7xl mx-auto px-6 md:px-12 relative">
        <div className="grid grid-cols-1 md:grid-cols-12 gap-12 md:gap-16 items-center">
          {/* Left - Image in oval */}
          <motion.div
            initial={{ opacity: 0, x: -40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="md:col-span-5 flex justify-center"
          >
            <div className="relative">
              {/* Background gradient oval */}
              <motion.div 
                animate={{ rotate: [0, 5, 0, -5, 0] }}
                transition={{ duration: 10, repeat: Infinity, ease: "easeInOut" }}
                className="absolute inset-0 bg-gradient-to-br from-[#5B8FB9]/20 via-[#7CB98F]/20 to-[#D4A5B5]/20 rounded-[60%_40%_55%_45%] transform scale-110" 
              />
              
              {/* Floating decorative dots */}
              <motion.div 
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 3, repeat: Infinity }}
                className="absolute -top-4 left-1/4 w-4 h-4 bg-[#5B8FB9] rounded-full" 
              />
              <motion.div 
                animate={{ y: [0, 8, 0] }}
                transition={{ duration: 4, repeat: Infinity }}
                className="absolute -bottom-2 right-1/4 w-3 h-3 bg-[#D4A5B5] rounded-full" 
              />
              <motion.div 
                animate={{ x: [0, 5, 0] }}
                transition={{ duration: 3.5, repeat: Infinity }}
                className="absolute top-1/3 -right-2 w-2 h-2 bg-[#7CB98F] rounded-full" 
              />
              
              {/* Image */}
              <div className="relative w-64 h-80 lg:w-80 lg:h-96 rounded-[50%_50%_45%_45%] overflow-hidden shadow-xl shadow-[#5B8FB9]/10">
                <img
                  src="https://images.unsplash.com/photo-1552664730-d307ca884978?auto=format&fit=crop&w=800&q=80"
                  alt="Leadership workshop"
                  className="w-full h-full object-cover"
                  data-testid="about-image-v2"
                />
              </div>
              
              {/* Experience badge */}
              <motion.div 
                initial={{ scale: 0 }}
                animate={isInView ? { scale: 1 } : {}}
                transition={{ delay: 0.5, type: "spring" }}
                className="absolute -bottom-4 -right-4 bg-gradient-to-r from-[#5B8FB9] to-[#7CB98F] text-white px-6 py-4 rounded-full shadow-lg"
              >
                <span className="text-3xl font-bold" style={{ fontFamily: "'Cormorant Garamond', serif" }}>15+</span>
                <span className="text-xs block">Years</span>
              </motion.div>
            </div>
          </motion.div>

          {/* Right - Text Content */}
          <motion.div
            initial={{ opacity: 0, x: 40 }}
            animate={isInView ? { opacity: 1, x: 0 } : {}}
            transition={{ duration: 0.8, delay: 0.2 }}
            className="md:col-span-7"
          >
            <span className="inline-block px-4 py-2 bg-gradient-to-r from-[#5B8FB9]/10 to-[#7CB98F]/10 text-[#5B8FB9] text-xs font-bold uppercase tracking-[0.2em] rounded-full mb-6 border border-[#5B8FB9]/20">
              About Susana
            </span>
            
            <h2 
              className="text-4xl sm:text-5xl font-light tracking-tight leading-tight text-gray-900 mb-8"
              style={{ fontFamily: "'Cormorant Garamond', serif" }}
              data-testid="about-headline-v2"
            >
              Leadership Rooted in{' '}
              <span className="bg-gradient-to-r from-[#5B8FB9] to-[#7CB98F] bg-clip-text text-transparent">Experience</span>
            </h2>
                
            <p className="text-lg text-gray-600 font-light leading-relaxed mb-6" data-testid="about-text-1-v2">
              With over 15 years of experience in corporate environments, I've seen 
              firsthand the challenges leaders and teams face — from high pressure 
              and burnout to lack of clarity and alignment.
            </p>
            
            <p className="text-base text-gray-600 font-light leading-relaxed mb-6" data-testid="about-text-2-v2">
              Today, I work with leaders, founders, and organizations to build 
              sustainable, high-performing teams by integrating leadership development 
              with workplace well-being.
            </p>
            
            <p className="text-base text-gray-600 font-light leading-relaxed" data-testid="about-text-3-v2">
              My approach combines strategic thinking, human-centered leadership, 
              and proven frameworks to help individuals and organizations thrive — 
              not just perform.
            </p>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

export default AboutSectionV2;
